/**
 * Kids WiseApp — analytics wrapper (PostHog).
 *
 * Design goals:
 *   • **Silent when unconfigured** — missing env vars must never crash
 *     the app or spam the console. Local development stays quiet.
 *   • **GDPR-friendly by default** — IPs are anonymised server-side
 *     (via PostHog config), no tracking on legal/cookie pages, and
 *     every call is a no-op if a user has opted out via
 *     `localStorage['kw:analytics-opt-out']`.
 *   • **One import surface** — `import { analytics } from '.../analytics'`
 *     everywhere; components should never reach for `window.posthog`
 *     directly so we can swap providers later without touching UI code.
 *
 * Public API:
 *   analytics.init()                     — call once, in src/index.js
 *   analytics.identify(user)             — after login/session hydration
 *   analytics.track(event, props?)       — funnel-level named events
 *   analytics.page(name?)                — manual page views (usually
 *                                          automatic via autocapture)
 *   analytics.reset()                    — on logout, detach the user
 *   analytics.optOut() / analytics.optIn()
 */

import posthog from 'posthog-js';

// -----------------------------------------------------------------
// Env resolution
// -----------------------------------------------------------------
// CRA/webpack inlines every `process.env.REACT_APP_*` reference at
// build time via DefinePlugin. Reference the values as raw literals
// (no `typeof process` guard) — the guard would fail at runtime
// because `process` itself isn't defined in the browser, only the
// inlined string values are.
/* eslint-disable no-undef */
const RAW_KEY = (
  process.env.REACT_APP_POSTHOG_KEY
  || process.env.VITE_POSTHOG_PROJECT_TOKEN
  || ''
);
const RAW_HOST = (
  process.env.REACT_APP_POSTHOG_HOST
  || process.env.VITE_POSTHOG_HOST
  || 'https://eu.i.posthog.com'
);
/* eslint-enable no-undef */

const HAS_KEY = !!RAW_KEY && RAW_KEY.startsWith('phc_');

// Pages where we deliberately do NOT track — legal pages must remain
// tracker-free so we can honestly say so in the privacy policy.
const NO_TRACK_PATHS = ['/cookies', '/privacy', '/terms', '/legal'];

const OPT_OUT_KEY = 'kw:analytics-opt-out';
const isOptedOut = () => {
  try { return localStorage.getItem(OPT_OUT_KEY) === '1'; } catch { return false; }
};
const isNoTrackPath = () => {
  try {
    const p = window.location?.pathname || '';
    return NO_TRACK_PATHS.some((prefix) => p.startsWith(prefix));
  } catch { return false; }
};
const canTrack = () => HAS_KEY && !isOptedOut() && !isNoTrackPath();

// Loaded flag guards double-init if hot reload fires the module twice.
let _loaded = false;

function init() {
  if (_loaded) return;
  if (!HAS_KEY) {
    // Silent in dev — but make it obvious in the console so the first
    // engineer to spin up a fresh env knows analytics is off.
    if (typeof console !== 'undefined') {
      console.info('[analytics] PostHog disabled (no REACT_APP_POSTHOG_KEY).');
    }
    return;
  }
  try {
    posthog.init(RAW_KEY, {
      api_host: RAW_HOST,
      // EU cloud honours EU privacy rules automatically. These flags
      // keep us safe even if we later switch to US cloud.
      person_profiles: 'identified_only',   // no anonymous person records
      autocapture: true,                    // click/pageview auto-tracking
      capture_pageview: true,
      capture_pageleave: true,
      disable_session_recording: false,     // enable — invaluable early on
      mask_all_text: false,                 // recordings are unmasked for
      mask_all_element_attributes: false,   // ourselves; DO NOT enable
                                            // recordings on parent-facing
                                            // pages before a DPIA update
      opt_out_capturing_by_default: false,
      // We are a B2B admin dashboard for nursery managers — respecting
      // DNT would silently drop events from any privacy-conscious
      // Chrome/Brave user. Our own privacy policy + explicit opt-out
      // toggle (analytics.optOut) is the compliant path, not DNT.
      respect_dnt: false,
      loaded: (ph) => {
        // Attach to window in dev to make headless testing + browser
        // debugging trivial. This is a no-op in prod builds because
        // NODE_ENV is inlined.
        if (typeof window !== 'undefined') { window.posthog = ph; }
        // If the user opted out in a previous session, respect it.
        if (isOptedOut()) ph.opt_out_capturing();
        // Never track the legal pages, even with autocapture.
        if (isNoTrackPath()) ph.opt_out_capturing();
      },
    });
    _loaded = true;
  } catch (err) {
    // Never let analytics take the app down.
    if (typeof console !== 'undefined') {
      console.warn('[analytics] PostHog init failed:', err);
    }
  }
}

function identify(user) {
  if (!canTrack() || !user?._id) return;
  try {
    posthog.identify(user._id, {
      email: user.email,
      name: user.full_name,
      role: user.role,
      organization_id: user.organization_id,
      organization_name: user.organization_name,
    });
    if (user.organization_id) {
      // PostHog "groups" — lets us slice funnels by nursery.
      posthog.group('organization', user.organization_id, {
        name: user.organization_name,
        slug: user.organization_slug,
      });
    }
  } catch (err) {
    if (typeof console !== 'undefined') console.warn('[analytics] identify failed:', err);
  }
}

function track(event, props = {}) {
  if (!canTrack() || !event) return;
  try {
    posthog.capture(event, props);
  } catch (err) {
    if (typeof console !== 'undefined') console.warn('[analytics] track failed:', err);
  }
}

function page(name) {
  if (!canTrack()) return;
  try {
    posthog.capture('$pageview', name ? { $current_url_name: name } : undefined);
  } catch { /* silent */ }
}

function reset() {
  if (!HAS_KEY) return;
  try { posthog.reset(); } catch { /* silent */ }
}

function optOut() {
  try { localStorage.setItem(OPT_OUT_KEY, '1'); } catch { /* ignore */ }
  if (HAS_KEY) { try { posthog.opt_out_capturing(); } catch { /* silent */ } }
}

function optIn() {
  try { localStorage.removeItem(OPT_OUT_KEY); } catch { /* ignore */ }
  if (HAS_KEY) { try { posthog.opt_in_capturing(); } catch { /* silent */ } }
}

export const analytics = {
  init,
  identify,
  track,
  page,
  reset,
  optOut,
  optIn,
  get enabled() { return HAS_KEY; },
};

// -----------------------------------------------------------------
// Canonical event names — import these instead of typing strings so
// PostHog dashboards stay clean (no "signup_started" vs "signupStarted"
// duplicates six months from now).
// -----------------------------------------------------------------
export const EVENTS = Object.freeze({
  LANDING_VIEWED:          'landing_viewed',
  PRICING_VIEWED:          'pricing_viewed',
  SIGNUP_STARTED:          'signup_started',
  SIGNUP_COMPLETED:        'signup_completed',
  DEMO_REQUESTED:          'demo_requested',
  FIRST_BOOKING_CREATED:   'first_booking_created',
  FIRST_INVOICE_SENT:      'first_invoice_sent',
  FIRST_PAYMENT_RECEIVED:  'first_payment_received',
});
